import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Upload, CalendarCheck, Check } from "lucide-react";

const BANGALORE_AREAS = [
  "Whitefield", "Sarjapur Road", "Electronic City", "HSR Layout", "Koramangala",
  "Indiranagar", "Hebbal", "Yelahanka", "Jayanagar", "JP Nagar",
  "Bannerghatta Road", "KR Puram", "Marathahalli", "Bellandur",
];
const APARTMENTS = ["1BHK", "2BHK", "3BHK", "4BHK", "Villa"];
const KITCHEN_TYPES = [
  { id: "Straight", desc: "One-wall, compact" },
  { id: "L-shape", desc: "Most common in 2-3BHK" },
  { id: "U-shape", desc: "Maximum storage" },
  { id: "Island",  desc: "Open layouts, statement piece" },
  { id: "Parallel", desc: "Two parallel counters" },
];
const FINISH = [
  { id: "essential", label: "Basic",   tag: "₹1,400 /sqft", desc: "Laminate, MDF carcass, branded hardware." },
  { id: "premium",   label: "Premium", tag: "₹2,000 /sqft", desc: "Veneer accents, plywood carcass, Hettich/Hafele." },
  { id: "luxury",    label: "Luxury",  tag: "₹2,800 /sqft", desc: "PU + veneer, marine ply, Italian fittings & quartz." },
];
const BUDGETS = ["Under ₹10L", "₹10L - ₹15L", "₹15L - ₹25L", "₹25L - ₹40L", "₹40L - ₹50L+"];

const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");
const lakhs = (n) => "₹" + (n / 100000).toFixed(1) + "L";

const STEPS = ["Location", "Property Type", "Carpet Area", "Bedrooms", "Kitchen", "Add-ons", "Finish", "Budget", "Estimate"];

export default function CostEstimator() {
  const [step, setStep] = useState(1);
  const [location, setLocation] = useState("");
  const [customLocation, setCustomLocation] = useState("");
  const [bhk, setBhk] = useState("");
  const [size, setSize] = useState(1000);
  const [bedrooms, setBedrooms] = useState(0);
  const [kitchenType, setKitchenType] = useState("");
  const [wardrobes, setWardrobes] = useState(0);
  const [tvUnit, setTvUnit] = useState(false);
  const [poojaUnit, setPoojaUnit] = useState(false);
  const [falseCeiling, setFalseCeiling] = useState(false);
  const [finish, setFinish] = useState("");
  const [budget, setBudget] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [floorFile, setFloorFile] = useState(null);
  const [floorUploading, setFloorUploading] = useState(false);

  const isOutsideBangalore = !!(customLocation && location === "__custom__" &&
    !/bangalore|bengaluru|blr/i.test(customLocation) &&
    !BANGALORE_AREAS.some((a) => customLocation.toLowerCase().includes(a.toLowerCase())));

  const effectiveLocation = location === "__custom__" ? customLocation.trim() : location;

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim());
  const phoneValid = /^[6-9]\d{9}$/.test(contact.phone.replace(/\D/g, "").slice(-10));
  const nameValid = contact.name.trim().length >= 2;
  const contactValid = nameValid && emailValid && phoneValid;

  const canNext = (
    (step === 1 && effectiveLocation && !isOutsideBangalore) ||
    (step === 2 && bhk) ||
    (step === 3 && size >= 200) ||
    (step === 4 && bedrooms >= 0) ||
    (step === 5 && kitchenType) ||
    (step === 6) ||
    (step === 7 && finish) ||
    (step === 8 && budget && contactValid)
  );

  const setBhkAndBedrooms = (b) => {
    setBhk(b);
    const n = parseInt(b) || 0;
    setBedrooms(n || 0);
    setWardrobes(n || 0);
  };

  const submit = async () => {
    if (!contactValid) {
      toast.error("Please enter a valid name, email and 10-digit phone number.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/estimate", {
        city: `${effectiveLocation}, Bengaluru`, bhk, size_sqft: Number(size), bedrooms,
        kitchen_type: kitchenType, wardrobes_count: wardrobes,
        tv_unit: tvUnit, pooja_unit: poojaUnit,
        false_ceiling: falseCeiling, false_ceiling_sqft: falseCeiling ? Number(size) : null,
        finish_level: finish, preferred_budget: budget,
        name: contact.name.trim(), email: contact.email.trim(), phone: contact.phone.trim(),
        rooms: [],
      });
      setResult(data);
      setStep(9);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not estimate");
    } finally { setLoading(false); }
  };

  const uploadFloor = async (file) => {
    if (!file) return;
    setFloorUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "floorplans");
      await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Floor plan uploaded — our team will review.");
      setFloorFile(file);
    } catch (e) { toast.error("Upload failed"); }
    finally { setFloorUploading(false); }
  };

  return (
    <div data-testid="estimator-page" className="px-6 md:px-12 lg:px-24 py-20 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <p className="overline">Cost Estimator</p>
          <h1 className="font-heading text-4xl sm:text-6xl font-light tracking-[-0.01em] mt-4 leading-[1.05]">
            A clearer starting point <em className="italic">for your interiors.</em>
          </h1>
          <p className="mt-5 max-w-xl text-[#5C5752] leading-[1.85] text-base font-light">
            Tell us about your home, requirements and preferred finish. Get a transparent preliminary estimate to help you plan your interiors with confidence.
          </p>
          <p className="mt-3 text-sm text-[#5C5752] tracking-wide">Step {Math.min(step, 9)} of 9 · {STEPS[step-1]}</p>

          <div className="mt-6 grid grid-cols-9 gap-1.5">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-[3px] rounded-full transition-colors duration-500 ${step >= i+1 ? "bg-[#C9A24A]" : "bg-[#E8E2D5]"}`} />
            ))}
          </div>

          <div className="mt-12 card-soft p-8 md:p-12">
            {step === 1 && (
              <div data-testid="estimator-step-1">
                <p className="overline">Step 01</p>
                <h2 className="font-heading text-3xl mt-3">Which area of Bengaluru is your project located in?</h2>
                <p className="mt-3 text-sm text-[#5C5752] italic">
                  Dreamy Interiors currently provides premium interior design and turnkey execution services exclusively across Bengaluru.
                </p>
                <div className="mt-8 flex flex-wrap gap-2.5">
                  {BANGALORE_AREAS.map((a) => (
                    <button key={a} data-testid={`est-area-${a}`} onClick={() => { setLocation(a); setCustomLocation(""); }}
                      className={`px-5 py-2.5 text-sm rounded-full border transition-all ${location === a ? "bg-[#1C1A17] text-white border-[#1C1A17]" : "border-[#E8E2D5] hover:border-[#1C1A17]"}`}>{a}</button>
                  ))}
                  <button data-testid="est-area-other" onClick={() => setLocation("__custom__")}
                    className={`px-5 py-2.5 text-sm rounded-full border transition-all ${location === "__custom__" ? "bg-[#1C1A17] text-white border-[#1C1A17]" : "border-[#E8E2D5] hover:border-[#1C1A17]"}`}>Other / Type manually</button>
                </div>
                {location === "__custom__" && (
                  <div className="mt-6">
                    <label className="text-xs uppercase tracking-[0.25em] text-[#5C5752]">Your locality</label>
                    <input data-testid="est-location-custom" autoFocus className="input-line mt-2" placeholder="e.g. RR Nagar, Bengaluru" value={customLocation} onChange={(e) => setCustomLocation(e.target.value)} />
                    {isOutsideBangalore && (
                      <p data-testid="est-outside-warning" className="mt-4 text-sm text-[#A07F2D] bg-[#F5F0E6] border border-[#E8D9B3] rounded-xl p-4">
                        Currently we serve only Bengaluru. Please <a href="/contact" className="link-underline">contact us</a> if your project is nearby — we may still be able to assist.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div data-testid="estimator-step-2">
                <p className="overline">Step 02</p>
                <h2 className="font-heading text-3xl mt-3">Property type</h2>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {APARTMENTS.map((b) => (
                    <button key={b} data-testid={`est-bhk-${b}`} onClick={() => setBhkAndBedrooms(b)}
                      className={`py-5 text-sm rounded-xl border transition-all ${bhk === b ? "bg-[#1C1A17] text-white border-[#1C1A17]" : "border-[#E8E2D5] hover:border-[#1C1A17]"}`}>{b}</button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="estimator-step-3">
                <p className="overline">Step 03</p>
                <h2 className="font-heading text-3xl mt-3">Approximate carpet area</h2>
                <div className="mt-10">
                  <div className="font-heading text-6xl">{size} <span className="text-2xl text-[#5C5752]">sqft</span></div>
                  <input type="range" min="400" max="4000" step="50" value={size} onChange={(e) => setSize(e.target.value)} data-testid="est-size-slider" className="w-full mt-8 accent-[#C9A24A]" />
                  <div className="flex justify-between text-xs text-[#5C5752] mt-2"><span>400 sqft</span><span>4000 sqft</span></div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="estimator-step-4">
                <p className="overline">Step 04</p>
                <h2 className="font-heading text-3xl mt-3">Number of bedrooms</h2>
                <div className="mt-8 grid grid-cols-5 gap-3">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} data-testid={`est-bed-${n}`} onClick={() => setBedrooms(n)}
                      className={`py-5 text-base rounded-xl border transition-all ${bedrooms === n ? "bg-[#1C1A17] text-white border-[#1C1A17]" : "border-[#E8E2D5] hover:border-[#1C1A17]"}`}>{n}</button>
                  ))}
                </div>
                <p className="text-sm text-[#5C5752] mt-6">We&apos;ll add a full wardrobe for each bedroom by default. You can edit in the next step.</p>
              </div>
            )}

            {step === 5 && (
              <div data-testid="estimator-step-5">
                <p className="overline">Step 05</p>
                <h2 className="font-heading text-3xl mt-3">Kitchen type</h2>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {KITCHEN_TYPES.map((k) => (
                    <button key={k.id} data-testid={`est-kitchen-${k.id}`} onClick={() => setKitchenType(k.id)}
                      className={`p-5 text-left rounded-xl border transition-all ${kitchenType === k.id ? "bg-[#F5F0E6] border-[#1C1A17]" : "border-[#E8E2D5] hover:border-[#1C1A17]"}`}>
                      <span className="block font-medium">{k.id}</span>
                      <span className="block text-xs text-[#5C5752] mt-1">{k.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 6 && (
              <div data-testid="estimator-step-6">
                <p className="overline">Step 06</p>
                <h2 className="font-heading text-3xl mt-3">Add-ons</h2>
                <p className="mt-3 text-sm text-[#5C5752]">Pick what you'd like included. You can refine any of these later during the consultation.</p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-[#E8E2D5] bg-white p-5 flex items-center justify-between">
                    <div className="pr-3">
                      <p className="font-medium">Wardrobes</p>
                      <p className="text-xs text-[#5C5752] mt-1">Floor-to-ceiling, room-wise</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setWardrobes(Math.max(0, wardrobes-1))} className="h-9 w-9 rounded-full border border-[#E8E2D5] hover:border-[#1C1A17] flex items-center justify-center">−</button>
                      <span data-testid="est-wardrobes-count" className="w-6 text-center font-medium">{wardrobes}</span>
                      <button onClick={() => setWardrobes(wardrobes+1)} className="h-9 w-9 rounded-full border border-[#E8E2D5] hover:border-[#1C1A17] flex items-center justify-center">+</button>
                    </div>
                  </div>

                  {[
                    {k: "TV Unit & Console", sub: "Wall panelling + storage",   v: tvUnit, set: setTvUnit, id: "tv"},
                    {k: "Pooja Unit",         sub: "Traditional or contemporary", v: poojaUnit, set: setPoojaUnit, id: "pooja"},
                    {k: "False Ceiling",      sub: "Full home, with cove lights", v: falseCeiling, set: setFalseCeiling, id: "ceiling"},
                  ].map(({ k, sub, v, set, id }) => (
                    <label key={id} className={`rounded-2xl border p-5 flex items-center justify-between cursor-pointer transition-all ${v ? "border-[#1C1A17] bg-[#F5F0E6]" : "border-[#E8E2D5] bg-white hover:border-[#1C1A17]"}`}>
                      <div className="pr-3">
                        <p className="font-medium">{k}</p>
                        <p className="text-xs text-[#5C5752] mt-1">{sub}</p>
                      </div>
                      <button type="button" data-testid={`est-addon-${id}`} onClick={() => set(!v)} className={`h-7 w-12 rounded-full transition-colors shrink-0 ${v ? "bg-[#C9A24A]" : "bg-[#E8E2D5]"} relative`}>
                        <span className={`absolute top-1 h-5 w-5 bg-white rounded-full shadow transition-transform ${v ? "left-1 translate-x-5" : "left-1 translate-x-0"}`} />
                      </button>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {step === 7 && (
              <div data-testid="estimator-step-7">
                <p className="overline">Step 07</p>
                <h2 className="font-heading text-3xl mt-3">Finish level</h2>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {FINISH.map((f) => (
                    <button key={f.id} data-testid={`est-finish-${f.id}`} onClick={() => setFinish(f.id)}
                      className={`p-6 text-left rounded-2xl border transition-all ${finish === f.id ? "bg-[#1C1A17] text-white border-[#1C1A17]" : "border-[#E8E2D5] hover:border-[#1C1A17] bg-white"}`}>
                      <span className="block font-heading text-3xl">{f.label}</span>
                      <span className={`block text-xs uppercase tracking-[0.25em] mt-2 ${finish === f.id ? "text-[#E8D9B3]" : "text-[#A07F2D]"}`}>{f.tag}</span>
                      <span className={`block text-sm mt-3 ${finish === f.id ? "text-white/70" : "text-[#5C5752]"}`}>{f.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 8 && (
              <div data-testid="estimator-step-8">
                <p className="overline">Step 08</p>
                <h2 className="font-heading text-3xl mt-3">Preferred budget &amp; contact</h2>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {BUDGETS.map((b) => (
                    <button key={b} data-testid={`est-budget-${b}`} onClick={() => setBudget(b)}
                      className={`py-4 px-2 text-xs rounded-full border transition-all ${budget === b ? "bg-[#1C1A17] text-white border-[#1C1A17]" : "border-[#E8E2D5] hover:border-[#1C1A17]"}`}>{b}</button>
                  ))}
                </div>
                <div className="mt-10">
                  <p className="overline text-[#A07F2D]">Share your details to view estimate <span className="text-[#C9A24A]">*</span></p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div>
                      <input className={`input-line ${contact.name && !nameValid ? "border-b-red-400" : ""}`} placeholder="Full name *" value={contact.name} onChange={(e) => setContact({...contact, name: e.target.value})} data-testid="est-name" />
                      {contact.name && !nameValid && <p className="text-xs text-red-500 mt-1">Enter at least 2 characters</p>}
                    </div>
                    <div>
                      <input className={`input-line ${contact.email && !emailValid ? "border-b-red-400" : ""}`} type="email" placeholder="Email *" value={contact.email} onChange={(e) => setContact({...contact, email: e.target.value})} data-testid="est-email" />
                      {contact.email && !emailValid && <p className="text-xs text-red-500 mt-1">Enter a valid email</p>}
                    </div>
                    <div>
                      <input className={`input-line ${contact.phone && !phoneValid ? "border-b-red-400" : ""}`} type="tel" placeholder="10-digit phone *" value={contact.phone} onChange={(e) => setContact({...contact, phone: e.target.value})} data-testid="est-phone" />
                      {contact.phone && !phoneValid && <p className="text-xs text-red-500 mt-1">Enter a valid Indian mobile number</p>}
                    </div>
                  </div>
                  <p className="text-xs text-[#5C5752] mt-4">
                    We only use these to share your detailed estimate. No spam — ever.
                  </p>
                </div>
              </div>
            )}

            {step === 9 && result && (
              <div data-testid="estimator-result">
                <p className="overline text-[#A07F2D]">Your estimated budget</p>
                <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                  <h2 className="font-heading text-5xl sm:text-6xl">{lakhs(result.range_low)}</h2>
                  <span className="font-heading text-3xl text-[#5C5752]">—</span>
                  <h2 className="font-heading text-5xl sm:text-6xl">{lakhs(result.range_high)}</h2>
                </div>
                <p className="mt-3 text-[#5C5752]">Mid-point {inr(result.total)} incl. 18% GST · {bhk} · {size} sqft · <span className="capitalize">{finish}</span> finish · {effectiveLocation}, Bengaluru</p>

                <div className="mt-10 divide-y divide-[#E8E2D5] border-t border-b border-[#E8E2D5]">
                  {result.breakdown.map((b, i) => (
                    <div key={i} className="flex items-start justify-between gap-4 py-4 text-sm">
                      <div>
                        <p className="text-[#1C1A17] font-medium">{b.label}</p>
                        {b.detail && <p className="mt-1 text-xs text-[#5C5752]">{b.detail}</p>}
                      </div>
                      <span className="font-medium whitespace-nowrap">{inr(b.amount)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between py-3 text-sm">
                    <span className="text-[#5C5752]">Component Subtotal</span>
                    <span className="font-medium">{inr(result.subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 text-sm">
                    <span className="text-[#5C5752]">GST (18%)</span>
                    <span className="font-medium">{inr(result.gst)}</span>
                  </div>
                  <div className="flex items-center justify-between py-4 text-base">
                    <span className="font-medium">Estimated Total</span>
                    <span className="font-heading text-2xl">{inr(result.total)}</span>
                  </div>
                </div>

                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link to="/book" data-testid="estimator-book-cta" className="btn-gold justify-center">
                    <CalendarCheck size={16} /> Book a Consultation
                  </Link>
                  <label htmlFor="floor-est" className="btn-secondary justify-center cursor-pointer">
                    <Upload size={16} /> {floorFile ? "Floor plan uploaded ✓" : floorUploading ? "Uploading…" : "Upload Floor Plan"}
                    <input id="floor-est" type="file" hidden accept=".pdf,.jpg,.jpeg,.png" data-testid="estimator-floor-upload" onChange={(e) => uploadFloor(e.target.files?.[0])} />
                  </label>
                </div>

                <div data-testid="estimator-disclaimer" className="mt-10 rounded-2xl border border-[#E8D9B3] bg-[#F5F0E6] p-5">
                  <p className="overline text-[#A07F2D]">Disclaimer</p>
                  <p className="mt-2 text-sm text-[#5C5752] leading-relaxed">
                    This estimate is an approximate calculation based on your selections. For a detailed and accurate quotation, please <Link to="/contact" className="link-underline text-[#1C1A17] font-medium">contact Dreamy Interiors</Link>.
                  </p>
                </div>

                <button data-testid="estimator-restart" onClick={() => { setStep(1); setResult(null); }} className="mt-6 text-xs uppercase tracking-[0.25em] text-[#5C5752] hover:text-[#1C1A17]">← Estimate again</button>
              </div>
            )}
          </div>

          {step < 9 && (
            <div className="mt-8 flex items-center justify-between">
              <button data-testid="est-back" disabled={step === 1} onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] disabled:opacity-30">
                <ArrowLeft size={14} /> Back
              </button>
              {step < 8 ? (
                <button data-testid="est-next" disabled={!canNext} onClick={() => setStep(step + 1)} className={canNext ? "btn-primary" : "rounded-full bg-[#E8E2D5] text-[#5C5752] px-8 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2"}>
                  Next <ArrowRight size={14} />
                </button>
              ) : (
                <button data-testid="est-calculate" disabled={!canNext || loading} onClick={submit} className={canNext ? "btn-gold" : "rounded-full bg-[#E8E2D5] text-[#5C5752] px-8 py-3 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2"}>
                  {loading ? "Calculating…" : <>Calculate Estimate <ArrowRight size={14} /></>}
                </button>
              )}
            </div>
          )}
        </div>

        <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
          <div className="card-soft p-8 bg-[#F5F0E6]">
            <p className="overline">Your Selection</p>
            <dl className="mt-6 space-y-3.5 text-sm">
              {[
                ["Project Location", effectiveLocation],
                ["Property type", bhk],
                ["Carpet area", size ? `${size} sqft` : ""],
                ["Bedrooms", bedrooms || ""],
                ["Kitchen", kitchenType],
                ["Wardrobes", wardrobes ? `× ${wardrobes}` : ""],
                ["TV Unit", tvUnit ? "Yes" : ""],
                ["Pooja Unit", poojaUnit ? "Yes" : ""],
                ["False Ceiling", falseCeiling ? "Yes" : ""],
                ["Finish", finish],
                ["Budget", budget],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-[#5C5752]">{k}</dt>
                  <dd className="text-right capitalize">{v ? <span className="inline-flex items-center gap-1.5">{v} {step > 1 && k === "City" && v && <Check size={12} className="text-[#A07F2D]"/>}</span> : <span className="text-[#8A847D]">—</span>}</dd>
                </div>
              ))}
            </dl>
            <div className="border-t border-[#E8E2D5] mt-6 pt-6">
              <p className="overline">Estimated Range</p>
              <p className="font-heading text-3xl mt-2 text-[#1C1A17]">
                {result ? `${lakhs(result.range_low)} — ${lakhs(result.range_high)}` : "—"}
              </p>
              <p className="text-xs text-[#5C5752] mt-1">incl. GST</p>
            </div>
          </div>
          <p className="text-xs text-[#5C5752] mt-4 leading-relaxed">
            Estimates are indicative. Final quotation is shared after a site visit and design freeze.
          </p>
        </aside>
      </div>
    </div>
  );
}
